import React from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FormatColorResetRoundedIcon from '@mui/icons-material/FormatColorResetRounded';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import IconSelect from '../../Inputs/IconSelect';
import { attributeName } from './surfaceKey';

/**
 * Edits a value vocabulary: what each stored value is called, which icon it carries and what colour
 * it is tinted. Controlled - the whole vocabulary is handed back on every edit.
 *
 * `fields` names what the reader of the vocabulary actually reads, and only those are offered.
 *
 * Only a control's own positions are edited here. What a *stored* value is called and how it is drawn
 * belongs to the attribute, is declared once in the section descriptor and is merged into every
 * layout that names the key, so an attribute key is reported rather than offered: the editor loads
 * layouts unresolved, so the box would be blank, and anything typed into it would be saved onto this
 * layout — where the resolver then leaves it alone, permanently overriding the descriptor here.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.options - The vocabulary being edited.
 * @param {Function} props.onChange - Receives the edited vocabulary.
 * @param {Array} props.fields - Which of label, icon and color to offer.
 * @param {String} props.valueKey - The key the vocabulary belongs to, which decides whether it is ours to edit.
 *
 * @returns {React.JSX.Element}
 */
const OptionsEditor = function OptionsEditor({
  options = [],
  onChange = () => {},
  fields = ['label', 'icon', 'color'],
  valueKey = '',
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const theme = useTheme();

  if (attributeName(valueKey))
    return (
      <Typography variant="body2" sx={{ opacity: '60%' }}>
        {intl.messages.VALUE_MAP_ON_ATTRIBUTE}
      </Typography>
    );

  /**
   * What the swatch shows: the option's own colour, a palette token resolved through the theme, or
   * the colour the value would otherwise be tinted with.
   */
  const shownColor = (option) => {
    if (option.color?.startsWith('#')) return option.color;

    const token = option.color
      ?.split('.')
      .reduce((node, part) => node?.[part], theme.palette);

    return (
      token ??
      theme.custom.stringToColor(
        String(option.value ?? '').toUpperCase() || 'DEFAULT'
      )
    );
  };

  const updateOption = (index, changes) =>
    onChange(
      options.map((option, position) =>
        position === index ? { ...option, ...changes } : option
      )
    );

  const removeOption = (index) =>
    onChange(options.filter((_, position) => position !== index));

  return (
    <ColumnFlexBox gap="12px">
      {options.map((option, index) => (
        <CenteredFlexBox key={`option-${String(index)}`} gap="8px">
          <TextField
            size="small"
            label={intl.messages.VALUE}
            value={option.value ?? ''}
            onChange={(e) => updateOption(index, { value: e.target.value })}
            sx={
              fields.includes('label')
                ? { width: '150px', flex: 'none' }
                : { flex: 1, minWidth: 0 }
            }
          />
          {fields.includes('label') && (
            <TextField
              size="small"
              label={intl.messages.LABEL}
              value={option.label ?? ''}
              onChange={(e) => updateOption(index, { label: e.target.value })}
              sx={{ flex: 1, minWidth: 0 }}
            />
          )}
          {fields.includes('icon') && (
            <IconSelect
              value={option.icon ?? ''}
              onChange={(name) => updateOption(index, { icon: name })}
            />
          )}
          {fields.includes('color') && (
            <>
              <Tooltip
                arrow
                title={
                  option.color
                    ? `${intl.messages.COLOR}: ${option.color}`
                    : intl.messages.DERIVED_COLOR
                }
              >
                <input
                  type="color"
                  aria-label={intl.messages.COLOR}
                  value={shownColor(option)}
                  onChange={(e) =>
                    updateOption(index, { color: e.target.value })
                  }
                  style={{
                    width: '44px',
                    height: '38px',
                    padding: 0,
                    border: option.color ? 'none' : '2px dashed',
                    background: 'none',
                    cursor: 'pointer',
                    flex: 'none',
                  }}
                />
              </Tooltip>
              <Tooltip arrow title={intl.messages.DERIVED_COLOR}>
                <span>
                  <IconButton
                    id={`clear-color-${String(index)}`}
                    size="small"
                    disabled={!option.color}
                    onClick={() => updateOption(index, { color: undefined })}
                  >
                    <FormatColorResetRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <IconButton
            id={`remove-option-${String(index)}`}
            size="small"
            onClick={() => removeOption(index)}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </CenteredFlexBox>
      ))}
      <CenteredFlexBox>
        <Button
          id="add-option-btn"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() =>
            onChange([
              ...options,
              fields.includes('label')
                ? { value: '', label: '' }
                : { value: '' },
            ])
          }
        >
          {translate('ADD_VALUE')}
        </Button>
      </CenteredFlexBox>
    </ColumnFlexBox>
  );
};

export default OptionsEditor;
